import type { ExpandableMenuItem, ExpandableMenuList } from '@pesto/ui/components/Navigation/Navigation.types';

import { stringifyFiltersToSearchParams } from '@pesto/utils';
import {
  Asterisk,
  Banknote,
  BookOpen,
  FileText,
  HandCoins,
  Handshake,
  Landmark,
  LayoutDashboard,
  MoreHorizontal,
  Scale,
  Search,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  TrendingUp,
  Users,
  Wrench,
} from 'lucide-react';

import type {
  LedgerTransactionsQueryVariables,
  ListLoansQueryVariables,
  ListUsersQueryVariables,
} from '../__generated__/graphql/api';

import { LedgerTransactionEntryScope, LoanStatus, TransactionViewMode, UserRole } from '../__generated__/graphql/api';
import { IS_PROD } from '../constants/global-env';
import { routes } from '../constants/routes';

const USERS_DEFAULT_FILTERS: Partial<ListUsersQueryVariables> = {
  roleSelections: [UserRole.User],
  isFriendsAndFam: false,
};

const LOANS_DEFAULT_FILTERS: Partial<ListLoansQueryVariables> = {
  statusSelections: [LoanStatus.Boarded],
};

const ADMINS_DEFAULT_FILTERS: Partial<ListUsersQueryVariables> = {
  roleSelections: [UserRole.PartnerAdmin, UserRole.SupportAgent],
};

const LEDGER_TRANSACTIONS_DEFAULT_FILTERS: Pick<LedgerTransactionsQueryVariables, 'entryScope' | 'viewMode'> = {
  entryScope: LedgerTransactionEntryScope.UserAccountEntry,
  viewMode: TransactionViewMode.Client,
};

const LEDGER_TRANSACTIONS_DEFAULT_HREF = `${routes.ledgerTransactions}?${stringifyFiltersToSearchParams(
  LEDGER_TRANSACTIONS_DEFAULT_FILTERS,
)}`;

const filterMenuItemsByPermissions = (menuItems: ExpandableMenuList, userRole: UserRole): ExpandableMenuList => {
  if (userRole === UserRole.SuperAdmin) {
    return menuItems;
  }

  return menuItems.filter((item: ExpandableMenuItem) => {
    return item.permissions?.includes(userRole);
  });
};

export const buildSCMenuLinks = (currentRole: UserRole): ExpandableMenuList => {
  const homeMenuItem: ExpandableMenuItem =
    currentRole === UserRole.SuperAdmin
      ? { type: 'item', href: routes.dashboard, label: 'Dashboard', icon: LayoutDashboard }
      : {
          type: 'item',
          href: routes.userSearch,
          label: 'Homepage',
          icon: Search,
          permissions: [UserRole.SupportAgent],
        };

  const allMenuLinks: ExpandableMenuList = [
    homeMenuItem,
    {
      type: 'item',
      href: routes.userSearch,
      label: 'Agent Search',
      icon: Search,
    },
    {
      type: 'item',
      href: `${routes.loans}?${stringifyFiltersToSearchParams(LOANS_DEFAULT_FILTERS)}`,
      label: 'Loans',
      icon: Landmark,
    },
    {
      type: 'item',
      href: `${routes.users}?${stringifyFiltersToSearchParams(USERS_DEFAULT_FILTERS)}`,
      label: 'Users',
      icon: Users,
    },
    {
      type: 'item',
      href: `${routes.admins}?${stringifyFiltersToSearchParams(ADMINS_DEFAULT_FILTERS)}`,
      label: 'Admins',
      icon: ShieldCheck,
    },
    {
      type: 'item',
      href: routes.achRequests,
      label: 'ACH Requests',
      icon: Banknote,
    },
    {
      type: 'item',
      href: routes.pendingDrawdowns,
      label: 'Pending Drawdowns',
      icon: HandCoins,
    },
    {
      type: 'item',
      href: routes.statements,
      label: 'Statements',
      icon: Wrench,
    },
    {
      type: 'item',
      href: routes.devices,
      label: 'Devices',
      icon: Smartphone,
    },
    {
      type: 'item',
      href: routes.partners,
      label: 'Partners',
      icon: Handshake,
    },
    {
      type: 'item',
      href: routes.programs,
      label: 'Programs',
      icon: Users,
    },
    {
      type: 'item',
      href: routes.primeRates,
      label: 'Prime Rates',
      icon: TrendingUp,
    },
    {
      type: 'item',
      href: routes.reconciliation,
      label: 'Reconciliation',
      icon: ShieldAlert,
    },
    {
      type: 'item',
      href: routes.disputes,
      label: 'Disputes',
      icon: Scale,
    },
    {
      type: 'item',
      href: routes.reports,
      label: 'Reports',
      icon: FileText,
    },
    {
      type: 'sub',
      label: 'Ledger',
      icon: BookOpen,
      items: [
        { type: 'item', label: 'Platform Accounts', href: routes.platformAccounts },
        { type: 'item', label: 'Ledger Transactions', href: LEDGER_TRANSACTIONS_DEFAULT_HREF },
        { type: 'item', label: 'Velocity Controls', href: routes.velocityControls },
      ],
    },
    {
      type: 'sub',
      label: 'Other',
      icon: MoreHorizontal,
      items: [
        { type: 'item', label: 'Admin Actions', href: routes.adminActions },
        { type: 'item', label: 'Loan Data', href: routes.loanData },
      ],
    },
    {
      type: 'sub',
      label: 'Development',
      icon: Asterisk,
      items: [
        { type: 'item', label: 'Oban Jobs', href: routes.obanJobs },
        { type: 'item', label: 'Statement Studio', href: routes.statementStudio },
        { type: 'item', label: 'Timeline Runs', href: routes.timelineRuns },
      ],
    },
  ];

  const filteredMenuLinks = () => {
    if (IS_PROD) {
      return allMenuLinks.filter((item) => item.label !== 'Development');
    }

    return allMenuLinks;
  };

  return filterMenuItemsByPermissions(filteredMenuLinks(), currentRole);
};
